import { useMemo } from "react"

const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute("content") : ""
}

// Attempt to parse the response body as JSON, falling back to null. Used
// on error responses so the thrown Error can carry structured info (e.g.
// 422 validation details like { error, missing: [...] }).
async function safeJsonOrNull(response) {
  try {
    const text = await response.text()
    return text ? JSON.parse(text) : null
  } catch {
    return null
  }
}

export default function useApi() {
  // Stable reference across renders — otherwise hooks that depend on it
  // (useEffect, useCallback) re-run every render and can loop forever.
  return useMemo(() => {
    const request = async (path, options = {}) => {
      const { method = "GET", body } = options

      const headers = {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      }

      const config = { method, headers, credentials: "same-origin" }
      if (body) config.body = JSON.stringify(body)

      const response = await fetch(path, config)
      if (!response.ok) {
        // Attach parsed body + status so callers can render structured
        // feedback (e.g. 422 { missing: [...] }). Message format kept
        // for existing consumers that only read `err.message`.
        const payload = await safeJsonOrNull(response)
        const err = new Error(`${response.status} ${response.statusText}`)
        err.status = response.status
        err.payload = payload
        throw err
      }
      if (response.status === 204) return null
      return response.json()
    }

    return {
      get: (path) => request(path),
      post: (path, body) => request(path, { method: "POST", body }),
      patch: (path, body) => request(path, { method: "PATCH", body }),
      put: (path, body) => request(path, { method: "PUT", body }),
      destroy: (path) => request(path, { method: "DELETE" })
    }
  }, [])
}
